import { useEffect, useMemo, useRef } from 'react';
import {
  CROP_HISTORY_PREVIEW_FETCH_CONCURRENCY,
  cropHistoryPreviewMatches,
  fetchCropHistoryPreviewMeta,
  hasResolvedCropHistoryPreview,
  resolveCropPreviewFromTaskResult,
} from '@/image-crop';
import { isTaskInProgress } from '@/pages/ipe/workbench/utils/taskProgress';

function buildPreviewPatch(task, meta, formatTask) {
  if (!meta?.url || cropHistoryPreviewMatches(task, meta)) return null;
  const formatter = typeof formatTask === 'function' ? formatTask : (row) => row;
  return formatter({
    ...task,
    previewUrl: meta.url,
    previewViewUrl: meta.viewUrl,
    previewDisplayUrl: meta.displayUrl,
    _outputPreview: meta.url,
  });
}

function applyPreviewPatches(setTasks, patchMap) {
  if (!patchMap?.size || typeof setTasks !== 'function') return;
  setTasks((prev) => {
    let changed = false;
    const next = prev.map((row) => {
      const patch = patchMap.get(String(row.id));
      if (!patch) return row;
      if (
        patch.previewUrl === row.previewUrl
        && patch.previewViewUrl === row.previewViewUrl
        && patch.previewDisplayUrl === row.previewDisplayUrl
        && patch._outputPreview === row._outputPreview
      ) {
        return row;
      }
      changed = true;
      return patch;
    });
    return changed ? next : prev;
  });
}

function resolveScrollRoot(panelRoot) {
  if (!panelRoot) return null;
  return panelRoot.querySelector('.wf-tasks-table-wrap') || panelRoot;
}

function needsBatchPreview(task) {
  if (!task?.id) return false;
  if (isTaskInProgress(task.status)) return false;
  return !hasResolvedCropHistoryPreview(task);
}

/**
 * 裁剪历史预览：同步补进行中行的 inline 预览；缺预览的终态行仅对可视区（+overscan）有界并发拉 batch。
 */
export function useCropHistoryVisiblePreview({
  enabled,
  tasks,
  rootRef,
  formatTask,
  setTasks,
  attemptedRef,
  concurrency = CROP_HISTORY_PREVIEW_FETCH_CONCURRENCY,
} = {}) {
  const formatTaskRef = useRef(formatTask);
  formatTaskRef.current = formatTask;

  const tasksByIdRef = useRef(new Map());
  const queueRef = useRef([]);
  const inflightRef = useRef(new Set());
  const pumpingRef = useRef(false);

  const pageKey = useMemo(
    () => (Array.isArray(tasks) ? tasks : [])
      .map((task) => String(task?.id || ''))
      .filter(Boolean)
      .join('|'),
    [tasks],
  );

  const syncKey = useMemo(
    () => (Array.isArray(tasks) ? tasks : [])
      .map((task) => [
        task?.id || '',
        task?.status || '',
        hasResolvedCropHistoryPreview(task) ? '1' : '0',
        resolveCropPreviewFromTaskResult(task) || '',
      ].join(':'))
      .join('|'),
    [tasks],
  );

  useEffect(() => {
    const map = new Map();
    (Array.isArray(tasks) ? tasks : []).forEach((task) => {
      if (task?.id != null) map.set(String(task.id), task);
    });
    tasksByIdRef.current = map;
  }, [tasks]);

  // 进行中：仅用 result 内联预览，不打 batch
  useEffect(() => {
    if (!enabled) return undefined;
    const patchMap = new Map();
    (Array.isArray(tasks) ? tasks : []).forEach((task) => {
      if (!task?.id || !isTaskInProgress(task.status)) return;
      const inline = resolveCropPreviewFromTaskResult(task);
      if (!inline) return;
      const patch = buildPreviewPatch(
        task,
        { url: inline, viewUrl: inline, displayUrl: inline },
        formatTaskRef.current,
      );
      if (patch) patchMap.set(String(task.id), patch);
    });
    applyPreviewPatches(setTasks, patchMap);
    return undefined;
  }, [enabled, syncKey, setTasks, tasks]);

  // 终态缺预览：可视行有界并发拉 batch
  useEffect(() => {
    if (!enabled || !rootRef?.current || !pageKey) return undefined;

    const cancelFlag = { current: false };
    const panelRoot = rootRef.current;
    const scrollRoot = resolveScrollRoot(panelRoot);
    const attempted = attemptedRef?.current || new Set();
    const inflight = inflightRef.current;

    queueRef.current = [];
    inflight.clear();

    const pump = async () => {
      if (pumpingRef.current || cancelFlag.current) return;
      pumpingRef.current = true;
      try {
        const limit = Math.max(1, Number(concurrency) || CROP_HISTORY_PREVIEW_FETCH_CONCURRENCY);
        while (!cancelFlag.current && queueRef.current.length > 0) {
          const batchIds = [];
          while (batchIds.length < limit && queueRef.current.length > 0) {
            const id = queueRef.current.shift();
            if (!id || inflight.has(id)) continue;
            if (attempted.has(id)) continue;
            const task = tasksByIdRef.current.get(id);
            if (!needsBatchPreview(task)) {
              attempted.add(id);
              continue;
            }
            attempted.add(id);
            inflight.add(id);
            batchIds.push(id);
          }
          if (!batchIds.length) break;

          await Promise.all(batchIds.map(async (id) => {
            try {
              const task = tasksByIdRef.current.get(id);
              if (!task || cancelFlag.current) return;
              let meta = null;
              try {
                meta = await fetchCropHistoryPreviewMeta(task);
              } catch {
                const inline = resolveCropPreviewFromTaskResult(task);
                meta = inline
                  ? { url: inline, viewUrl: inline, displayUrl: inline }
                  : null;
              }
              if (cancelFlag.current || !meta?.url) return;
              const latest = tasksByIdRef.current.get(id) || task;
              const patch = buildPreviewPatch(latest, meta, formatTaskRef.current);
              if (patch) applyPreviewPatches(setTasks, new Map([[id, patch]]));
            } finally {
              inflight.delete(id);
            }
          }));
        }
      } finally {
        pumpingRef.current = false;
        if (!cancelFlag.current && queueRef.current.length > 0) {
          void pump();
        }
      }
    };

    const enqueue = (taskId) => {
      const id = String(taskId || '').trim();
      if (!id || cancelFlag.current) return;
      if (attempted.has(id) || inflight.has(id)) return;
      if (queueRef.current.includes(id)) return;
      const task = tasksByIdRef.current.get(id);
      if (!needsBatchPreview(task)) return;
      queueRef.current.push(id);
      void pump();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.getAttribute('data-history-task-id');
          if (id) enqueue(id);
        });
      },
      {
        root: scrollRoot,
        rootMargin: '160px 0px',
        threshold: 0.01,
      },
    );

    const rows = panelRoot.querySelectorAll('[data-history-task-id]');
    rows.forEach((row) => observer.observe(row));

    return () => {
      cancelFlag.current = true;
      observer.disconnect();
      queueRef.current = [];
      inflight.clear();
      pumpingRef.current = false;
    };
  }, [enabled, pageKey, rootRef, setTasks, attemptedRef, concurrency]);
}

export default useCropHistoryVisiblePreview;
